import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Chip,
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
  
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility"; // Import Visibility icon
import { useNavigate } from "react-router-dom";
import {
  getAllCustomerService,
  searchCustomerService,
} from "../../services/customerService";
import moment from "moment";
import useStore from "../../useStore";

const statusColor = (status) => {
  return status === "active" || status === "Active" ? "success" : "error";
};

const CustomerTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { setEditUserId, setIsUpdate, permissions } = useStore();
  const [filterStatus, setFilterStatus] = useState("");
  const editPermission = permissions.find(
    (perm) => perm.path === "/edit-user"
  );
  console.log(editPermission)
  const handleFilterChange = (value) => {
    console.log(value);
    setFilterStatus(value);
    setPage(0); // Reset pagination when changing filters
  };

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = searchQuery
        ? await searchCustomerService(
            page,
            rowsPerPage,
            searchQuery,
            false,
            filterStatus
          )
        : await getAllCustomerService(page, rowsPerPage, false, filterStatus);

      setCustomers(response?.customersWithDetails || []);
      setTotalCustomers(response?.totalCustomers || 0);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage, filterStatus]);

  const handleSearchChange = useCallback((event) => {
    const query = event.target.value;
    setSearchQuery(query);

    // Reset to the first page when starting a new search
    setPage(0);
  }, []);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchCustomers();
    }, 2000);

    return () => clearTimeout(debounceTimeout); // Cleanup timeout on unmount or re-render
  }, [fetchCustomers]);

  const handleViewClick = (customerId) => {
    // Store the customer _id (you can use sessionStorage, localStorage, context, or state)
    sessionStorage.setItem("viewedCustomerId", customerId);

    // Navigate to the customer detail page or handle as needed
    navigate(`/customer/${customerId}`);
  };
  const handleEditClick = (customerId) => {
    // Store the customer _id (you can use sessionStorage, localStorage, context, or state)
    setEditUserId(customerId);
    setIsUpdate(true);

    // Navigate to the customer detail page or handle as needed
    navigate(`/customer/create-customer`);
  };
  const handleNameClick = (customerId) => {
    // Store the customer _id (you can use sessionStorage, localStorage, context, or state)
    sessionStorage.setItem("viewedCustomerId", customerId);

    // Navigate to the customer detail page or handle as needed
    navigate(`/customer/overview/${customerId}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          m: 2,
        }}
      >
        <Box sx={{ width: "50%" }}>
          <Typography variant="h6" gutterBottom>
            Search Customers
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or phone number"
            onChange={handleSearchChange}
            value={searchQuery}
          />
        </Box>

        <Box>
          <Button
            variant="contained"
            size="small"
            sx={{
              mx: 1,
              textTransform: "capitalize",
              fontSize: "16px",
              borderRadius: "8px",
            }}
            onClick={() => {
              setIsUpdate(false);
              navigate("create-customer");
            }}
          >
            Create Loan Customer
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              mx: 1,
              textTransform: "capitalize",
              fontSize: "16px",
              borderRadius: "8px",
            }}
            onClick={() => navigate("create-pigmy-customer")}
          >
            Create Pigmy Customer
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1, m: 2 }}>
        <Chip
          label="Verified"
          onClick={() => handleFilterChange("verified")}
          color={filterStatus === "verified" ? "primary" : "default"}
          sx={{ cursor: "pointer" }}
        />
        <Chip
          label="Rejected"
          onClick={() => handleFilterChange("rejected")}
          color={filterStatus === "rejected" ? "secondary" : "default"}
          sx={{ cursor: "pointer" }}
        />
        <Chip
          label="All"
          onClick={() => handleFilterChange("all")}
          color={filterStatus === "all" ? "primary" : "default"}
          sx={{ cursor: "pointer" }}
        />
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Sl. No.
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="left">
              Customer Name
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Phone Number
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Created At
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Verification Status
            </TableCell>
            {/* <TableCell sx={{ color: "#5252ff" }} align="center">
              Loan Latest Collection Date
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Loan Pigmy Collection Date
            </TableCell> */}
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Loan Status
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Pigmy Status
            </TableCell>
            <TableCell sx={{ color: "#5252ff" }} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map((customer, index) => (
            <TableRow
              key={customer._id}
              sx={{
                backgroundColor: customer.isRejected ? "#FFCCCC" : "inherit", // Light red for rejected customers
              }}
            >
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                {page * rowsPerPage + index + 1}
              </TableCell>
              <TableCell
                sx={{
                  padding: "5px",
                  margin: "0px",
                  color: "primary.main", // Primary color for text
                  cursor: "default", // Default cursor
                  "&:hover": {
                    cursor: "pointer", // Change to pointer on hover
                    textDecoration: "underline", // Optional: Add underline effect
                  },
                }}
                align="left"
                onClick={() => handleNameClick(customer._id)}
              >
                {customer.userName}
              </TableCell>

              <TableCell
                sx={{ padding: "5px", margin: "0px", wordWrap: "normal" }}
                align="center"
              >
                {customer.contact?.contactValue || "N/A"}
              </TableCell>
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                {moment(customer.createdAt).format("DD-MM-YYYY")}
              </TableCell>
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                <Chip
                  label={customer?.isVerified ? "Verified" : "Not Verified"}
                  size="small"
                  sx={{
                    width: "70%",
                    padding: "0",
                    margin: "0px",
                    backgroundColor: customer?.isVerified ? "green" : "red",
                    color: "white", // Ensure text is readable
                  }}
                />
              </TableCell>
              {/* <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                {customer.loanLatestDate || "N/A"}
              </TableCell>
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                {customer.loanPigmyDate || "N/A"}
              </TableCell> */}
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                <Chip
                  label={customer.loans[0]?.status || "Inactive"}
                  color={statusColor(customer.loans[0]?.status)}
                  size="small"
                  sx={{ width: "80%", padding: "0", margin: "0px" }}
                />
              </TableCell>
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                <Chip
                  label={customer.pigmys[0]?.status || "Inactive"}
                  color={statusColor(customer.pigmys[0]?.status)}
                  size="small"
                  sx={{ width: "70%", padding: "0", margin: "0px" }}
                />
              </TableCell>
              <TableCell sx={{ padding: "5px", margin: "0px" }} align="center">
                {editPermission?.access && (
                  <IconButton
                    color="primary"
                    sx={{ padding: "5px", margin: "0px" }}
                    onClick={() => handleEditClick(customer._id)}
                  >
                    <EditIcon />
                  </IconButton>
                )}

                {/* <IconButton
                  color="secondary"
                  sx={{ padding: "5px", margin: "0px" }}
                >
                  <DeleteIcon />
                </IconButton> */}
                <IconButton
                  color="default"
                  sx={{ padding: "5px", margin: "0px" }}
                  onClick={() => handleViewClick(customer._id)}
                >
                  <VisibilityIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 20, 30]}
        component="div"
        count={totalCustomers}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};

export default CustomerTable;
